import { DocumentTextIcon, QueueListIcon } from '@heroicons/react/24/outline';
import type { ClassSession } from 'pollworker/src/components/ScheduledClasses';
import { getScheduledClassDetail } from 'pollworker/src/fetchers';
import React, { useEffect, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle, Tabs, TabsContent, TabsList, TabsTrigger } from 'shared/src/components/ui/new';
import { DetailsPanel } from './ModalScheduledClassDetailsPanel';
import { DocumentsPanel } from './ModalScheduledClassDocumentsPanel';

type FetchClassDetailFn = (sessionId: string) => Promise<ClassSession>;
type FetchPrintDocumentFn = (sessionId: string, evUserId: string) => Promise<Blob>;

type ManageScheduledClassProps = {
  scheduledClassId: string;
  showScheduleView: boolean;
  setShowScheduleView: (show: boolean) => void;
  setShowDropClassView: (show: boolean) => void;
  fetchClassDetail?: FetchClassDetailFn;
  fetchPrintDocument?: FetchPrintDocumentFn;
  allowDrop?: boolean;
};

export function ManageScheduledClass({
  scheduledClassId,
  showScheduleView,
  setShowScheduleView,
  setShowDropClassView,
  fetchPrintDocument,
  fetchClassDetail: fetchFn = getScheduledClassDetail,
  allowDrop = true,
}: ManageScheduledClassProps) {
  const [currentTab, setCurrentTab] = useState<'Details' | 'Documents'>('Details');
  const [loading, setLoading] = useState<boolean>(true);
  const [classDetails, setClassDetails] = useState<ClassSession>();

  useEffect(() => {
    if (!scheduledClassId) return;

    setLoading(true);
    fetchFn(scheduledClassId)
      .then((response) => {
        setClassDetails(response);
      })
      .finally(() => setLoading(false));
  }, [scheduledClassId, fetchFn]);

  return (
    <Dialog
      size="md"
      open={showScheduleView}
      onOpenChange={(open) => {
        if (!open) setShowScheduleView(false);
      }}
    >
      <DialogContent className="p-0 gap-0 overflow-hidden max-w-xl" disableScrollArea>
        <DialogHeader>
          <DialogTitle>{!loading ? classDetails?.ClassName : 'Class Session'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Tabs value={currentTab} onValueChange={(value) => setCurrentTab(value as 'Details' | 'Documents')}>
            <TabsList className="w-full [&>button]:flex-1">
              <TabsTrigger value="Details" data-testid="tab-details" className="gap-1.5">
                <QueueListIcon className="size-4" aria-hidden="true" />
                Details
              </TabsTrigger>
              <TabsTrigger value="Documents" data-testid="tab-documents" className="gap-1.5">
                <DocumentTextIcon className="size-4" aria-hidden="true" />
                Documents
              </TabsTrigger>
            </TabsList>
            <TabsContent value="Details" className="mt-4">
              <DetailsPanel
                scheduledClassId={scheduledClassId}
                classDetails={classDetails}
                setShowScheduleView={setShowScheduleView}
                setShowDropClassView={setShowDropClassView}
                fetchPrintDocument={fetchPrintDocument}
                allowDrop={allowDrop}
              />
            </TabsContent>
            <TabsContent value="Documents" className="mt-4">
              <DocumentsPanel classDetails={classDetails} />
            </TabsContent>
          </Tabs>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
